import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import {
  ArrowLeft, CheckCircle2, Lock, BookOpen, FileText, Award,
  GraduationCap, Trophy, Target, ChevronRight, ChevronDown, Play, ChevronsLeft, ChevronsRight, Home, LogOut, HelpCircle
} from 'lucide-react';
import '../../styles/CursosVortex.css';
import { useCourseProgress } from '../../hooks/useCourseProgress';
import { normalizeCourse, getTemas, countCompletedTemas, isTemaCompletado, isCourseCompletado, getLessonFlashcards } from '../../utils/courseStructure';
import { LessonContent } from './LessonContent';
import { CertificateCard } from './CertificateCard';

export const VirtualClassroom = ({ course, uid, userName = 'Estudiante', userEmail = '', onBackToCatalog, isSubscribed = true }) => {
  const navigate = useNavigate();
  const normCourse = useMemo(() => normalizeCourse(course), [course]);
  const temas = useMemo(() => getTemas(normCourse), [normCourse]);

  const { progress, updateProgress } = useCourseProgress(normCourse?.id, uid);

  // Lección o vista activa
  const [active, setActive] = useState(null); // { type: 'lesson', lesson, tema } | { type: 'certificate' }
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [expandedTemas, setExpandedTemas] = useState({});

  // Inicializar con la primera lección del primer tema y abrir módulos
  useEffect(() => {
    if (temas.length > 0 && temas[0].lessons?.length > 0) {
      setActive({ type: 'lesson', lesson: temas[0].lessons[0], tema: temas[0] });
      const allOpen = {};
      temas.forEach(t => { allOpen[t.id] = true; });
      setExpandedTemas(allOpen);
    }
  }, [normCourse?.id]);

  // Mantener el módulo de la lección activa expandido automáticamente
  useEffect(() => {
    if (active?.tema?.id) {
      setExpandedTemas(prev => ({ ...prev, [active.tema.id]: true }));
    }
  }, [active?.tema?.id]);

  const toggleTema = (temaId) => {
    setExpandedTemas(prev => ({
      ...prev,
      [temaId]: prev[temaId] === undefined ? false : !prev[temaId]
    }));
  };

  const expandAllTemas = () => {
    const all = {};
    temas.forEach(t => { all[t.id] = true; });
    setExpandedTemas(all);
  };

  const collapseAllTemas = () => {
    const all = {};
    temas.forEach(t => { all[t.id] = false; });
    setExpandedTemas(all);
  };

  const temasProg = progress?.temas || {};
  const totalTemas = temas.length;
  const totalLessons = temas.reduce((acc, t) => acc + (t.lessons?.length || 0), 0);

  // Contar lecciones completadas
  const completedLessonsCount = useMemo(() => {
    let count = 0;
    temas.forEach((t) => {
      const p = temasProg[t.id]?.lessons || {};
      count += Object.keys(p).filter((k) => p[k]).length;
    });
    return count;
  }, [temas, temasProg]);

  const progressPct = totalLessons > 0 ? Math.round((completedLessonsCount / totalLessons) * 100) : 0;
  const isCourseDone = progressPct >= 100 || isCourseCompletado(normCourse, progress);

  // Si no está suscrito (defensa en profundidad)
  if (!isSubscribed) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '440px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Lock size={32} style={{ color: 'var(--gold-matte)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0 }}>Acceso no disponible</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
            Para ingresar a las lecciones y recursos interactivos de este curso, necesitas adquirir el Paquete Activo de 4 Cursos ($300 MXN).
          </p>
          <button onClick={onBackToCatalog} className="btn-vx-action" style={{ alignSelf: 'center', padding: '10px 20px' }}>
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }

  const handleSelectLesson = (tema, lesson) => {
    setActive({ type: 'lesson', lesson, tema });
    // Marcar lección como vista en el progreso
    const currentTemaProg = temasProg[tema.id] || {};
    const currentLessons = currentTemaProg.lessons || {};
    if (!currentLessons[lesson.id]) {
      updateProgress({
        temas: {
          ...temasProg,
          [tema.id]: {
            ...currentTemaProg,
            lessons: { ...currentLessons, [lesson.id]: true }
          }
        }
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenCertificate = () => {
    setActive({ type: 'certificate' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExitToHome = () => navigate('/');
  const handleLogout = async () => {
    try { await signOut(auth); } catch (e) {}
    navigate('/');
  };

  return (
    <div className="vx-classroom-container">

      {/* ── Top Bar del Aula Virtual ── */}
      <div className="vx-classroom-header">
        <button onClick={onBackToCatalog} className="btn-vx-back">
          <ArrowLeft className="w-4 h-4" />
          <span>Volver al Catálogo</span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden' }}>
          <span className="vx-card-cat-badge" style={{ position: 'static' }}>
            {normCourse?.category}
          </span>
          <h2 style={{ fontFamily: 'var(--font-tactical)', fontSize: '1rem', color: '#fff', margin: 0 }} className="truncate">
            {normCourse?.title}
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button onClick={handleExitToHome} className="btn-vx-back" title="Volver al inicio">
            <Home className="w-4 h-4" />
            <span>Inicio</span>
          </button>
          <button onClick={handleLogout} className="btn-vx-back btn-vx-danger" title="Cerrar sesión">
            <LogOut className="w-4 h-4" />
            <span>Salir</span>
          </button>
        </div>
      </div>

      {/* ── Grid Principal: Navegación de Módulos + Contenido ── */}
      <div className={`vx-classroom-grid${navCollapsed ? ' vx-classroom-grid--collapsed' : ''}`}>

        {/* Botón de expandir si está contraído */}
        {navCollapsed ? (
          <button
            type="button"
            onClick={() => setNavCollapsed(false)}
            className="vx-nav-expand-btn"
            title="Expandir panel de módulos"
          >
            <ChevronsRight size={20} />
            <span>Módulos</span>
          </button>
        ) : (
          <div className="vx-classroom-nav">

            {/* Cabecera del Panel de Navegación */}
            <div style={{
              background: '#0f2035', border: '1px solid rgba(197, 160, 89, 0.2)',
              borderRadius: '14px', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--gold-matte)', fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '0.04em' }}>
                  📚 MÓDULOS DEL CURSO
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={expandAllTemas}
                    style={{
                      background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.25)',
                      color: 'var(--gold-matte)', cursor: 'pointer', fontSize: '0.64rem', padding: '2px 6px',
                      borderRadius: '4px', fontWeight: 600
                    }}
                    title="Desplegar todos los módulos"
                  >
                    + Todos
                  </button>
                  <button
                    type="button"
                    onClick={collapseAllTemas}
                    style={{
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.64rem', padding: '2px 6px',
                      borderRadius: '4px', fontWeight: 600
                    }}
                    title="Contraer todos los módulos"
                  >
                    - Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setNavCollapsed(true)}
                    className="vx-nav-collapse-btn"
                    title="Contraer el panel"
                  >
                    <ChevronsLeft size={14} />
                    <span>Contraer</span>
                  </button>
                </div>
              </div>

              {/* Barra de Progreso del Estudiante */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', fontFamily: 'monospace', fontWeight: 'bold' }}>
                    PROGRESO GENERAL
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'monospace', color: progressPct === 100 ? '#34d399' : 'var(--gold-matte)' }}>
                    {progressPct}%
                  </span>
                </div>
                <div style={{ height: '8px', borderRadius: '99px', background: 'rgba(10, 26, 42, 0.8)', overflow: 'hidden', border: '1px solid rgba(197,160,89,0.15)' }}>
                  <div style={{
                    height: '100%', borderRadius: '99px', width: `${progressPct}%`,
                    background: progressPct === 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #c5a059, #8f6f2f)',
                    transition: 'width 0.4s ease'
                  }} />
                </div>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace', display: 'block', marginTop: '6px' }}>
                  {completedLessonsCount} de {totalLessons} lecciones completadas
                </span>
              </div>

              {/* Botón Certificado */}
              {isCourseDone && (
                <button
                  onClick={handleOpenCertificate}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.4)', color: '#34d399', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    fontSize: '0.75rem', fontWeight: 'bold'
                  }}
                >
                  <Award size={16} />
                  <span>Ver Certificado de Acreditación</span>
                </button>
              )}
            </div>

            {/* Lista de Módulos (Temas) y Lecciones */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '74vh', overflowY: 'auto' }}>
              {temas.map((tema, tIdx) => {
                const currentTemaProg = temasProg[tema.id]?.lessons || {};
                const temaDone = (tema.lessons || []).every(l => currentTemaProg[l.id]);
                const isExpanded = expandedTemas[tema.id] !== false;

                return (
                  <div key={tema.id || tIdx} style={{
                    border: '1px solid rgba(197,160,89,0.2)',
                    background: '#0a1a2a',
                    borderRadius: '10px', overflow: 'hidden'
                  }}>
                    {/* Encabezado del Módulo con Toggle */}
                    <div
                      onClick={() => toggleTema(tema.id)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                        padding: '12px 14px', background: 'rgba(15, 32, 53, 0.7)',
                        borderBottom: isExpanded ? '1px solid rgba(197,160,89,0.15)' : 'none',
                        cursor: 'pointer', userSelect: 'none', transition: 'background 0.2s'
                      }}
                      title={isExpanded ? 'Clic para contraer módulo' : 'Clic para desplegar módulo'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                        <span style={{
                          width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, fontSize: '0.72rem',
                          fontWeight: 'bold', fontFamily: 'monospace', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: temaDone ? 'rgba(16,185,129,0.25)' : 'rgba(197,160,89,0.2)',
                          color: temaDone ? '#6ee7b7' : 'var(--gold-matte)'
                        }}>
                          {temaDone ? <CheckCircle2 size={14} /> : tIdx + 1}
                        </span>
                        <p style={{
                          margin: 0, fontSize: '0.84rem', fontWeight: 'bold',
                          color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                          {tema.title}
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                          {tema.lessons?.length || 0} lecc.
                        </span>
                        <span style={{ color: 'var(--gold-matte)', display: 'flex', alignItems: 'center' }}>
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </span>
                      </div>
                    </div>

                    {/* Lista de Lecciones del Módulo: Auto-adaptable y con Scroll si excede */}
                    {isExpanded && (
                      <div
                        className="vx-module-lessons-scroll"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: 'auto',
                          maxHeight: (tema.lessons?.length || 0) > 4 ? '380px' : 'none',
                          overflowY: (tema.lessons?.length || 0) > 4 ? 'auto' : 'visible'
                        }}
                      >
                        {(tema.lessons || []).map((lesson, lIdx) => {
                          const isLessonActive = active?.type === 'lesson' && active.lesson.id === lesson.id;
                          const isLessonDone = !!currentTemaProg[lesson.id];
                          const quizCount = (lesson.quiz || []).length;

                          return (
                            <div
                              key={lesson.id || lIdx}
                              onClick={() => handleSelectLesson(tema, lesson)}
                              style={{
                                padding: '12px 14px', cursor: 'pointer',
                                borderBottom: lIdx < tema.lessons.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                display: 'flex', alignItems: 'center', gap: '10px',
                                background: isLessonActive ? 'rgba(197, 160, 89, 0.15)' : 'transparent',
                                borderLeft: isLessonActive ? '3px solid var(--gold-matte)' : '3px solid transparent',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              <Play size={13} style={{ color: isLessonActive ? 'var(--gold-matte)' : 'var(--text-muted)', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                  margin: 0, fontSize: '0.8rem',
                                  color: isLessonActive ? 'var(--gold-matte)' : '#e2e8f0',
                                  fontWeight: isLessonActive ? 700 : 500,
                                  lineHeight: 1.35,
                                  wordBreak: 'break-word'
                                }}>
                                  {lesson.title}
                                </p>
                                <span style={{ fontSize: '0.66rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                  {quizCount > 0 ? `${quizCount} reactivos Ceneval` : 'Lección teórica'}
                                </span>
                              </div>
                              {isLessonDone && <CheckCircle2 size={14} style={{ color: '#34d399', flexShrink: 0 }} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        )}

        {/* ── Contenido Central del Aula Virtual ── */}
        <div className="vx-classroom-content">
          {active?.type === 'certificate' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
              <CertificateCard
                courseTitle={normCourse?.title}
                studentName={userName}
                date={new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
              />
              <button
                onClick={() => {
                  if (temas[0]?.lessons?.[0]) {
                    setActive({ type: 'lesson', lesson: temas[0].lessons[0], tema: temas[0] });
                  }
                }}
                className="btn-vx-action"
                style={{ padding: '8px 20px', fontSize: '0.85rem' }}
              >
                Volver a las lecciones
              </button>
            </div>
          ) : active?.type === 'lesson' && active.lesson ? (
            <LessonContent lesson={active.lesson} />
          ) : (
            <div style={{
              background: '#0f2035', border: '1px solid rgba(197,160,89,0.2)', borderRadius: '14px',
              padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'
            }}>
              <BookOpen size={48} style={{ color: 'var(--gold-matte)' }} />
              <h3 style={{ color: '#fff', margin: 0 }}>Selecciona una lección</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                Elige cualquier lección del menú de la izquierda para comenzar a estudiar con videos, puntos clave y reactivos Ceneval.
              </p>
            </div>
          )}
        </div>

      </div>

    </div>
  );
};
